import ApiDatabase from 'server';
import { DEFAULT_CRM_SCENARIO, buildCrmMock } from './crmMockData';

/**
 * What the CRM demo keeps while the app runs: a database per demo state
 * (`?mock=`), built on first use, and the real lists it reads from the api
 * (the companies, the groups), kept a moment. server/crm/crmApi.js and
 * crmRecordApi.js answer from here.
 */

export const LATENCY = { read: 400, write: 250 };
export const later = (ms, run) => setTimeout(run, ms);
export const copy = (value) => JSON.parse(JSON.stringify(value));
const ANONYMOUS = { id: 'me', firstname: 'Moi', lastname: '' };

const databases = new Map();
export const dbOf = ({ scenario = DEFAULT_CRM_SCENARIO, me = ANONYMOUS } = {}) => {
  if (!databases.has(scenario)) databases.set(scenario, buildCrmMock(scenario, me));
  return databases.get(scenario);
};
export const meOf = (payload) => payload?.me ?? ANONYMOUS;

// A read of the demo: `chargement` never answers, `erreur` fails.
export const read = (payload, resolve, reject, view) => {
  if (payload?.scenario === 'chargement') return;
  if (payload?.scenario === 'erreur') {
    later(LATENCY.read, () => reject(new Error('Mock: the CRM api failed')));
    return;
  }
  const db = dbOf(payload);
  later(LATENCY.read, () => {
    try {
      resolve(copy(view(db, meOf(payload))));
    } catch (error) {
      reject(error);
    }
  });
};
export const write = (payload, resolve, reject, change) => {
  try {
    const result = change(dbOf(payload), meOf(payload));
    later(LATENCY.write, () => resolve(copy(result ?? null)));
  } catch (error) {
    later(LATENCY.write, () => reject(error));
  }
};

// A call of server/index.js as a promise. Two reads of one endpoint at once
// would cancel each other: each gets its own `abortKey`.
export const ask = (method, payload, abortKey) =>
  new Promise((resolve, reject) =>
    abortKey ? ApiDatabase[method](payload, resolve, reject, { abortKey }) : ApiDatabase[method](payload, resolve, reject),
  );

const KEEP = 2 * 60 * 1000;
const kept = new Map();
// A real list, kept two minutes under `key`; a failed read is not kept.
const keep = (key, load) => {
  const now = Date.now();
  kept.forEach((entry, other) => now - entry.at >= KEEP && kept.delete(other));
  if (kept.has(key)) return kept.get(key).value;
  const value = load();
  kept.set(key, { at: now, value });
  value.catch(() => kept.delete(key));
  return value;
};

// A real list read again at its next use (after a write that changes it).
export const forget = (prefix) => kept.forEach((entry, key) => key.startsWith(prefix) && kept.delete(key));

// Every company of a search at once, for the demo's record pages and goals.
const ALL_COMPANIES = 5000;
export const realCompanies = (query = {}) =>
  keep(`companies:${JSON.stringify(query)}`, () =>
    ask('getCompanies', { ...query, page: 1, limit: ALL_COMPANIES }).then((data) => data?.companies ?? []),
  );

/**
 * The groups (big accounts) and their companies. The list of companies does
 * not say which group a company is in (its first $project drops
 * `bigAccounts`, api/useCases/admin/searchCompanies.usecase.js), so the
 * groups are read once and each company finds its own here.
 */
export const realGroups = () =>
  keep('groups', () =>
    ask('getBigAccounts', { search: '' }).then((data) =>
      (data?.bigAccounts ?? []).map((group) => ({
        id: String(group._id),
        name: group.name,
        companyIds: (group.companies ?? []).map((link) => String(link.id_company)),
      })),
    ),
  );

// The group of each company: { [companyId]: { id, name } }.
export const bigAccountsById = () =>
  realGroups().then((groups) =>
    Object.fromEntries(groups.flatMap((group) => group.companyIds.map((id) => [id, { id: group.id, name: group.name }]))),
  );
